import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  InputBase,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Business as BusinessIcon,
  SearchOutlined,
  MoreVert,
  EditOutlined,
  DeleteOutline,
  FileDownloadOutlined,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import apiService from "../../service/Api";
import Pagination from "../../components/common/Pagination";
import { accentForKey } from "../../constants/crmAccents";
import { toast } from "react-toastify";
import { IContact } from "../../types/contact";
import { downloadVCard, vCardFilename } from "../contacts/utils/vCard";

interface ICompany {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  industry?: string;
  logo?: string;
}
interface CompanyApiResponse {
  id: number;
  name: string;
  primary_email?: string | null;
  phone_number?: string | null;
  city?: string | null;
  country?: string | null;
  street_address?: string | null;
  company_type?: string | null;
  company_logo?: string | null;
}

const ListCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [isExporting, setIsExporting] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const primaryHoverColor = reduxTheme.primary_colors?.[600] || "#6b4e3d";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[100] || "#D6DDEB";
  const backgroundColor = reduxTheme.neutral_colors?.[900] || "#A9B2C3";

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    company: ICompany,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedCompany(company);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCompany(null);
  };

  const handleEditCompany = (companyId: number) => {
    navigate(`/companies/${companyId}/edit`);
    handleMenuClose();
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!companyId) return;

    // Optional: confirm deletion with the user
    const confirmed = window.confirm(
      "Are you sure you want to delete this company?",
    );
    if (!confirmed) return;

    try {
      await apiService.delete(`api/companies/company/${companyId}/`);
      // Refresh the list after deletion
      fetchCompanies();
    } catch (err) {
      console.error("Failed to delete company:", err);
      toast("Failed to delete company. Please try again.");
    } finally {
      handleMenuClose();
    }
  };

  const companyToContact = (company: ICompany): IContact => ({
    id: company.id,
    name: company.name,
    first_name: null,
    last_name: null,
    email: company.email || null,
    phone: company.phone || null,
    salutation: null,
    contact_source: null,
    contact_type: "company",
    contactPerson: null,
    contactPersonAvatar: null,
    companyLogo: company.logo || null,
    industry: company.industry || null,
    website: null,
    taxNumber: null,
    dateOfBirth: null,
    city: null,
    completeness_score: 0,
    country: null,
    facebook_profile: null,
    phone_numbers: null,
    address: company.address || null,
    instagram_profile: null,
    twitter_profile: null,
    linkedin_profile: null,
    notes: null,
    profile_image_file: company.logo || null,
    category_type: null,
    company_id: null,
    company_name: company.name,
    company_email: company.email || null,
    created_at: new Date().toISOString(),
    created_by: null,
    updated_at: undefined,
    last_updated_by: null,
    type: "company",
    contact_image: company.logo || undefined,
  });

  const handleExportCompany = (company: ICompany) => {
    const contact = companyToContact(company);
    downloadVCard([contact], vCardFilename(contact));
    try {
      toast.success("Exported company vCard");
    } catch (e) {
      console.warn("Toast error:", e);
    }
  };

  const handleExportAllCompanies = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const exportCompanies: IContact[] = [];
      let page = 1;
      let nextPage: string | null = null;

      do {
        const params: Record<string, string | number | undefined> = {
          page,
          page_size: 1500,
          search: searchTerm || undefined,
        };

        const result = await apiService.get<{
          count: number;
          results: CompanyApiResponse[];
          next: string | null;
        }>("api/companies/company/", params);

        exportCompanies.push(
          ...result.data.results.map((company) =>
            companyToContact({
              id: company.id,
              name: company.name,
              email: company.primary_email || undefined,
              phone: company.phone_number || undefined,
              address:
                company.city || company.country
                  ? `${company.city || ""}, ${company.country || ""}`
                      .trim()
                      .replace(/^,|,$/g, "")
                  : company.street_address || undefined,
              industry: company.company_type || undefined,
              logo: company.company_logo || undefined,
            }),
          ),
        );
        nextPage = result.data.next;
        page += 1;
      } while (nextPage);

      if (exportCompanies.length === 0) {
        setError("No companies found to export.");
        return;
      }

      downloadVCard(
        exportCompanies,
        `muzukuru-companies-${new Date().toISOString().slice(0, 10)}.vcf`,
      );
      try {
        toast.success(`${exportCompanies.length} companies exported`);
      } catch (e) {
        console.warn("Toast error:", e);
      }
    } catch (err) {
      console.error("Company export failed:", err);
      setError("Failed to export companies. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number | undefined> = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
      };

      const res = await apiService.get<{
        count: number;
        results: CompanyApiResponse[];
      }>("api/companies/company/", params);

      const mappedCompanies: ICompany[] = res.data.results.map((company) => ({
        id: company.id,
        name: company.name,
        email: company.primary_email || undefined,
        phone: company.phone_number || undefined,
        address:
          company.city || company.country
            ? `${company.city || ""}, ${company.country || ""}`
                .trim()
                .replace(/^,|,$/g, "")
            : company.street_address || undefined,
        industry: company.company_type || undefined,
        logo: company.company_logo || undefined,
      }));

      setCompanies(mappedCompanies);
      setTotalItems(res.data.count);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to load companies.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const filteredCompanies = companies; // API already handles search

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Search & controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: "background.paper",
            border: `1.5px solid ${primaryColor}33`,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            pl: 1,
            flex: 1,
            maxWidth: 400,
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:focus-within": {
              borderColor: primaryColor,
              boxShadow: `0 0 0 3px ${primaryColor}22`,
            },
          }}
        >
          <SearchOutlined sx={{ mr: 1, color: primaryColor }} />
          <InputBase
            placeholder="Search companies..."
            sx={{ ml: 1, flex: 1, color: "text.primary" }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
          <IconButton
            onClick={() => setViewMode("grid")}
            sx={{
              color: viewMode === "grid" ? primaryColor : secondaryTextColor,
            }}
          >
            <GridViewIcon />
          </IconButton>
          <IconButton
            onClick={() => setViewMode("list")}
            sx={{
              color: viewMode === "list" ? primaryColor : secondaryTextColor,
            }}
          >
            <ViewListIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/companies/add")}
            sx={{
              bgcolor: primaryColor,
              "&:hover": { bgcolor: primaryHoverColor },
              color: "#fff",
            }}
          >
            Add Company
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlined />}
          onClick={handleExportAllCompanies}
          disabled={isLoading || isExporting}
          sx={{
            borderColor: primaryColor,
            color: primaryColor,
            "&:hover": {
              borderColor: primaryHoverColor,
              color: primaryHoverColor,
            },
          }}
        >
          {isExporting ? "Exporting..." : "Export Companies"}
        </Button>
      </Box>

      {/* Loading/Error */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
        >
          {error}
          <Button color="inherit" size="small" onClick={fetchCompanies}>
            Retry
          </Button>
        </Alert>
      )}

      {!isLoading && filteredCompanies.length === 0 && (
        <Typography sx={{ textAlign: "center", mt: 4 }}>
          No companies found.
        </Typography>
      )}

      {/* Grid view */}
      {viewMode === "grid" && filteredCompanies.length > 0 && (
        <>
          <Grid container spacing={2}>
            {filteredCompanies.map((company) => {
              const accent = accentForKey(company.industry || company.name);
              return (
                <Grid size={{ xs: 12, md: 3 }} key={company.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      pt: 2.5,
                      minHeight: 250,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      bgcolor: "background.paper",
                      border: `1px solid ${accent.border}`,
                      borderTop: `4px solid ${accent.solid}`,
                      borderTopWidth: 4,
                      position: "relative",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 10px 24px ${accent.border}`,
                      },
                      "&:hover .menu-button": {
                        opacity: 1,
                      },
                    }}
                  >
                    <IconButton
                      className="menu-button"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        opacity: 0,
                        transition: "opacity 0.2s",
                        bgcolor: "background.paper",
                        border: `1px solid ${accent.border}`,
                        "&:hover": { bgcolor: accent.bg },
                      }}
                      onClick={(e) => handleMenuClick(e, company)}
                    >
                      <MoreVert sx={{ color: accent.fg }} />
                    </IconButton>

                    {/* Company avatar/content */}
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        src={company.logo}
                        sx={{
                          width: 80,
                          height: 80,
                          mb: 2,
                          bgcolor: accent.bg,
                          color: accent.fg,
                          border: `2px solid ${accent.border}`,
                        }}
                      >
                        {!company.logo && <BusinessIcon fontSize="large" />}
                      </Avatar>
                      <Typography variant="h6" align="center" fontWeight={600}>
                        {company.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ color: "text.secondary", mb: 0.5 }}
                      >
                        {company.email || "-"}
                      </Typography>
                      <Chip
                        label={company.industry || "Company"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          textTransform: "capitalize",
                          bgcolor: accent.bg,
                          color: accent.fg,
                          border: `1px solid ${accent.border}`,
                        }}
                      />
                    </CardContent>

                    <CardActions
                      sx={{
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Button
                        onClick={() => navigate(`/companies/${company.id}`)}
                        variant="contained"
                        disableElevation
                        sx={{
                          bgcolor: accent.solid,
                          color: "#fff",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { bgcolor: accent.fg },
                        }}
                      >
                        View Company
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() =>
                selectedCompany && handleEditCompany(selectedCompany.id)
              }
            >
              <EditOutlined sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedCompany) {
                  handleExportCompany(selectedCompany);
                }
                handleMenuClose();
              }}
            >
              <FileDownloadOutlined sx={{ mr: 1 }} /> Export vCard
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedCompany && handleDeleteCompany(selectedCompany.id)
              }
            >
              <DeleteOutline sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>

          {!isLoading && (
            <Box sx={{ mt: 4 }}>
              <Pagination
                currentPage={currentPage - 1} // convert to 0-based for UI
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={(page) => setCurrentPage(page + 1)} // convert back to 1-based
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* List view */}
      {viewMode === "list" && filteredCompanies.length > 0 && (
        <Box>
          {filteredCompanies.map((company) => {
            const accent = accentForKey(company.industry || company.name);
            return (
              <Card
                key={company.id}
                sx={{
                  mb: 2,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  borderLeft: `5px solid ${accent.solid}`,
                  border: `1px solid ${accent.border}`,
                  borderLeftWidth: 5,
                  transition: "box-shadow 0.2s ease",
                  "&:hover": { boxShadow: `0 6px 18px ${accent.border}` },
                }}
              >
                <Avatar
                  src={company.logo}
                  sx={{
                    width: 50,
                    height: 50,
                    mr: 2,
                    bgcolor: accent.bg,
                    color: accent.fg,
                    border: `2px solid ${accent.border}`,
                  }}
                >
                  {!company.logo && <BusinessIcon />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {company.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {company.email}
                  </Typography>
                  <Chip
                    label={company.industry || "Company"}
                    size="small"
                    sx={{
                      mt: 0.5,
                      fontWeight: 700,
                      textTransform: "capitalize",
                      bgcolor: accent.bg,
                      color: accent.fg,
                      border: `1px solid ${accent.border}`,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    onClick={() => navigate(`/companies/${company.id}`)}
                    variant="contained"
                    disableElevation
                    sx={{
                      bgcolor: accent.solid,
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: accent.fg },
                    }}
                  >
                    View Company
                  </Button>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </motion.div>
  );
};

export default ListCompanies;
